import { ForwardedRef, forwardRef, useId } from 'react';

import { Button, ButtonSize, ButtonType, ButtonVariant } from '@xemplo/button';
import { DawnCross, DawnSearch } from '@xemplo/icons';
import { XemploInput } from '@xemplo/xemplo-input';

import { SearchFieldProps } from './search-field.types';

/**
 * The search field is a raw implementation of the `XemploInput` component
 * adding and limiting the styling defined by our Design System.
 * https://www.figma.com/design/46zj5HhFyRjL3veNSC9Q35/%E2%9A%99%EF%B8%8F-Dawn-UI?node-id=23037-27522&m=dev
 *
 * It's a controlled input field and as such, its state must be controlled
 * externally. We have a simple hook to manage the input state.
 */
export const SearchField = forwardRef(
  (props: SearchFieldProps, ref: ForwardedRef<HTMLInputElement>) => {
    const { value = '', onClear, ...restProps } = props;

    return (
      <XemploInput
        leadingIcon={<DawnSearch />}
        {...restProps}
        value={value}
        ref={ref}
        type="text"
        trailingIcon={value === '' ? undefined : <ClearIcon onClear={onClear} />}
        role="searchbox"
      />
    );
  }
);

function ClearIcon({ onClear }: Pick<SearchFieldProps, 'onClear'>) {
  const btnId = useId();
  return (
    <Button
      id={btnId}
      ariaLabel={`Clear search input ${btnId}`}
      size={ButtonSize.Small}
      type={ButtonType.Tertiary}
      variant={ButtonVariant.Dark}
      icon={<DawnCross />}
      onClick={onClear}
    />
  );
}
